import "./qa-premium.css";
import { useLocation } from "wouter";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";
import { DOMAIN_COLORS, useQaPerms } from "./qa-shared";
import { AreaChart, Area, RadarChart, Radar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
import { Calendar, FileText, CheckCircle2, Layers, BookCheck } from "lucide-react";
import { FioriPageHeader } from "@/components/fiori";

const fmtK = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n));
const shortLabel = (name: string) => name.split(/[ ,]/)[0];

export default function QaTemplateList() {
  const [, setLocation] = useLocation();
  const perms = useQaPerms();
  const { data: templates = [], isLoading } = trpc.qa.templates.list.useQuery();
  const { data: visits = [] } = trpc.qa.visits.list.useQuery({ limit: 200 });

  const createDraft = trpc.qa.templates.createDraftVersion.useMutation({
    onSuccess: (r) => { toast.success(r.reused ? "Opened existing draft" : "Draft version created"); setLocation(`/qa/templates/${r.versionId}`); },
    onError: (e) => toast.error(e.message),
  });

  const handleNewTemplate = () => {
    if (templates.length === 0) {
      toast.error("No templates available to create a draft from");
      return;
    }
    createDraft.mutate({ templateId: templates[0].template.id });
  };

  // --- Real KPI metrics -----------------------------------------------------
  const qcByVersion = new Map<number, number>(templates.map((t: any) => [t.versionId, t.questionCount ?? 0]));
  const totalVisits = visits.length;
  const approvedVisits = visits.filter((v: any) => v.status === "report_approved").length;
  const totalQuestions = visits.reduce((acc: number, v: any) => acc + (qcByVersion.get(v.templateVersionId) ?? 0), 0);
  const domainNames = new Set<string>();
  templates.forEach((t: any) => (t.domains ?? []).forEach((d: any) => domainNames.add(d.name)));
  const domainsCovered = domainNames.size;

  // --- Monthly visit activity (last 6 months, real counts) ------------------
  const monthly = (() => {
    const now = new Date();
    const buckets: { key: string; name: string; value: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, name: d.toLocaleString("en", { month: "short" }), value: 0 });
    }
    const idx = new Map(buckets.map((b, i) => [b.key, i]));
    visits.forEach((v: any) => {
      if (!v.visitDate) return;
      const dt = new Date(v.visitDate);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      const i = idx.get(key);
      if (i != null) buckets[i].value++;
    });
    return buckets;
  })();

  // --- Domain weighting radar (real weights per template) -------------------
  const allDomainNames = Array.from(new Set(templates.flatMap((t: any) => (t.domains ?? []).map((d: any) => d.name)))) as string[];
  const radarData = allDomainNames.map((name) => {
    const row: any = { name: shortLabel(name) };
    templates.forEach((t: any) => {
      const d = (t.domains ?? []).find((x: any) => x.name === name);
      row[t.template.name] = d ? Math.round(d.weightBps / 100) : 0;
    });
    return row;
  });

  // --- Per-template performance (real) --------------------------------------
  const visitsByVersion = new Map<number, any[]>();
  visits.forEach((v: any) => {
    const arr = visitsByVersion.get(v.templateVersionId) ?? [];
    arr.push(v);
    visitsByVersion.set(v.templateVersionId, arr);
  });
  const templateMetrics = templates.map((t: any) => {
    const tv = visitsByVersion.get(t.versionId) ?? [];
    const approved = tv.filter((v: any) => v.status === "report_approved").length;
    return {
      name: t.template.name,
      visits: tv.length,
      questions: t.questionCount ?? 0,
      domains: (t.domains ?? []).length,
      version: t.versionNumber ?? "—",
      completion: tv.length ? Math.round((approved / tv.length) * 100) : 0,
    };
  });

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      <FioriPageHeader
        title="Questionnaire Templates"
        subtitle="Template Analytics & Insights — In-depth analysis with multiple visualizations and detailed breakdowns"
        icon={<BookCheck className="h-5 w-5" />}
      />

      <div className="px-0 pb-4">
        {isLoading ? (
          <div className="loading"><span className="spin" /></div>
        ) : templates.length === 0 ? (
          <div className="empty"><i className="ti ti-shield-check" />No templates yet.</div>
        ) : (
          <>
            {/* KPI Summary Boxes */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-0 pb-4">
              <div className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3 transition-all hover:border-[#5B2C93]/30">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-[#E8DCF5] flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-[#5B2C93]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#6B6B6B] font-medium">Total Visits</p>
                    <p className="text-xl font-semibold text-[#2C2C2C]">{totalVisits}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3 transition-all hover:border-[#5B2C93]/30">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                    <FileText className="h-4 w-4 text-[#2563EB]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#6B6B6B] font-medium">Questions Asked</p>
                    <p className="text-xl font-semibold text-[#2C2C2C]">{fmtK(totalQuestions)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3 transition-all hover:border-[#5B2C93]/30">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
                    <CheckCircle2 className="h-4 w-4 text-[#059669]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#6B6B6B] font-medium">Approved Reports</p>
                    <p className="text-xl font-semibold text-[#2C2C2C]">{approvedVisits}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3 transition-all hover:border-[#5B2C93]/30">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
                    <Layers className="h-4 w-4 text-[#D97706]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#6B6B6B] font-medium">Domains Covered</p>
                    <p className="text-xl font-semibold text-[#2C2C2C]">{domainsCovered}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Section */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "24px" }}>
              <div style={{
                background: "#fff",
                border: "1px solid #ECE7F3",
                borderRadius: "16px",
                padding: "20px",
                boxShadow: "0 2px 10px rgba(79, 0, 140, 0.05)",
              }}>
                <h3 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "14px", color: "#1A1025" }}>Monthly Visit Activity</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={monthly}>
                    <defs>
                      <linearGradient id="tplActivityFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#5B2C93" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#5B2C93" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ECE7F3" />
                    <XAxis dataKey="name" stroke="#8B7DA8" />
                    <YAxis stroke="#8B7DA8" allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: "rgba(0, 0, 0, 0.8)", border: "none", borderRadius: "8px", color: "#fff" }} />
                    <Area type="monotone" dataKey="value" name="Visits" stroke="#5B2C93" strokeWidth={3} fill="url(#tplActivityFill)" dot={{ fill: "#5B2C93", r: 5 }} activeDot={{ r: 6 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div style={{
                background: "#fff",
                border: "1px solid #ECE7F3",
                borderRadius: "16px",
                padding: "20px",
                boxShadow: "0 2px 10px rgba(79, 0, 140, 0.05)",
              }}>
                <h3 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "14px", color: "#1A1025" }}>Domain Weighting Comparison</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#ECE7F3" />
                    <PolarAngleAxis dataKey="name" stroke="#8B7DA8" />
                    <PolarRadiusAxis stroke="#8B7DA8" />
                    {templates.map((t: any, i: number) => (
                      <Radar
                        key={t.template.id}
                        name={t.template.name}
                        dataKey={t.template.name}
                        stroke={DOMAIN_COLORS[i % DOMAIN_COLORS.length]}
                        fill={DOMAIN_COLORS[i % DOMAIN_COLORS.length]}
                        fillOpacity={0.15}
                      />
                    ))}
                    <Legend />
                    <Tooltip contentStyle={{ backgroundColor: "rgba(0, 0, 0, 0.8)", border: "none", borderRadius: "8px", color: "#fff" }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Analytics Table */}
            <div style={{
              background: "#fff",
              border: "1px solid #ECE7F3",
              borderRadius: "16px",
              padding: "20px",
              boxShadow: "0 2px 10px rgba(79, 0, 140, 0.05)",
              overflowX: "auto",
            }}>
              <h3 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "14px", color: "#1A1025" }}>Template Performance Metrics</h3>
              <table style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
              }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #ECE7F3" }}>
                    <th style={{ textAlign: "left", padding: "10px 8px", fontWeight: "700", textTransform: "uppercase", fontSize: "11px", color: "#8B7DA8", letterSpacing: "0.05em" }}>Template</th>
                    <th style={{ textAlign: "center", padding: "10px 8px", fontWeight: "700", textTransform: "uppercase", fontSize: "11px", color: "#8B7DA8", letterSpacing: "0.05em" }}>Visits</th>
                    <th style={{ textAlign: "center", padding: "10px 8px", fontWeight: "700", textTransform: "uppercase", fontSize: "11px", color: "#8B7DA8", letterSpacing: "0.05em" }}>Questions</th>
                    <th style={{ textAlign: "center", padding: "10px 8px", fontWeight: "700", textTransform: "uppercase", fontSize: "11px", color: "#8B7DA8", letterSpacing: "0.05em" }}>Domains</th>
                    <th style={{ textAlign: "center", padding: "10px 8px", fontWeight: "700", textTransform: "uppercase", fontSize: "11px", color: "#8B7DA8", letterSpacing: "0.05em" }}>Version</th>
                    <th style={{ textAlign: "center", padding: "10px 8px", fontWeight: "700", textTransform: "uppercase", fontSize: "11px", color: "#8B7DA8", letterSpacing: "0.05em" }}>Completion %</th>
                  </tr>
                </thead>
                <tbody>
                  {templateMetrics.map((m: any, idx: number) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #ECE7F3", cursor: "pointer", transition: "background 0.15s" }} onMouseEnter={(e) => e.currentTarget.style.background = "#F7F0FD"} onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "10px 8px", fontWeight: "600", color: "#1A1025" }}>{m.name}</td>
                      <td style={{ textAlign: "center", padding: "10px 8px", color: "#4B4060" }}>{m.visits}</td>
                      <td style={{ textAlign: "center", padding: "10px 8px", color: "#4B4060" }}>{m.questions.toLocaleString()}</td>
                      <td style={{ textAlign: "center", padding: "10px 8px", color: "#4B4060" }}>{m.domains}</td>
                      <td style={{ textAlign: "center", padding: "10px 8px", color: "#4B4060" }}>v{m.version}</td>
                      <td style={{ textAlign: "center", padding: "10px 8px", color: "#10B981", fontWeight: "600" }}>{m.completion}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
